import { useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Link,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { DownloadBundle, getDownloadUrls } from "../../api/workflows";
import { triggerDownloads } from "../../utils/download";

// Per-export presigned download links. Initially hidden behind a
// "Get download links" button (signing URLs costs an STS round-trip
// the user shouldn't pay until they want to download). Once the
// bundle is fetched, surfaces every file with a checkbox; the
// "Download selected" button fires `triggerDownloads`, which spawns
// a hidden iframe per URL (dodges Chrome's "multiple files" prompt
// per-file — see utils/download.ts).
//
// Two response shapes (resolved server-side based on workflow kind):
//   - Single export: flat parts[] + optional manifest_url.
//   - Per-month batch export: months[] each with its own parts[].
// The component renders either shape; the per-month variant uses a
// table with a tri-state per-row checkbox to select all files in
// one month at once.
interface Props {
  workflowId: string;
}

type DownloadItem = { url: string; label: string };

function bundleItems(bundle: DownloadBundle): DownloadItem[] {
  const items: DownloadItem[] = [];
  if (bundle.months) {
    for (const m of bundle.months) {
      for (const p of m.parts) {
        items.push({ url: p.url, label: p.download_name || p.name });
      }
      if (m.manifest_url) {
        items.push({
          url: m.manifest_url,
          label: m.manifest_download_name || "_manifest.json",
        });
      }
      if (m.index_url) {
        items.push({
          url: m.index_url,
          label: m.index_download_name || "_index.parquet",
        });
      }
    }
  } else {
    for (const p of bundle.parts || []) {
      items.push({ url: p.url, label: p.download_name || p.name });
    }
    if (bundle.manifest_url) {
      items.push({
        url: bundle.manifest_url,
        label: bundle.manifest_download_name || "_manifest.json",
      });
    }
    if (bundle.index_url) {
      items.push({
        url: bundle.index_url,
        label: bundle.index_download_name || "_index.parquet",
      });
    }
  }
  return items;
}

// Format the URL lifetime returned by the operator as a short
// "good for X" label. Used in the panel header so the user can tell
// at a glance whether they'll be 12h URLs (IRSA-signed) or 7d
// (dedicated-IAM-user-signed).
function formatExpiry(seconds: number): string {
  if (seconds >= 86400) {
    const days = Math.round(seconds / 86400);
    return `${days}d`;
  }
  if (seconds >= 3600) {
    const hours = Math.round(seconds / 3600);
    return `${hours}h`;
  }
  const mins = Math.round(seconds / 60);
  return `${mins}m`;
}

// Compose the clipboard text for "Copy URLs". One block per file with
// the suggested download filename + the URL, separated by blank
// lines. The customer pastes the message into their tool of choice
// (curl, wget, browser) and gets distinct filenames automatically
// because each URL carries response-content-disposition.
function clipboardTextForUrls(
  items: DownloadItem[],
  selected: Set<string>,
  expiresIn: number,
): string {
  const picked = items.filter((i) => selected.has(i.url));
  const lines: string[] = [];
  for (const it of picked) {
    lines.push(`# ${it.label}`);
    lines.push(it.url);
    lines.push("");
  }
  if (lines.length === 0) return "";
  const expiry = new Date(Date.now() + expiresIn * 1000).toUTCString();
  lines.push(`# URLs expire ${expiry} (${formatExpiry(expiresIn)} from now)`);
  return lines.join("\n");
}

export function DownloadLinksPanel({ workflowId }: Props) {
  const [bundle, setBundle] = useState<DownloadBundle | null>(null);
  const [bundleErr, setBundleErr] = useState<string | null>(null);
  // Multi-select for batched downloads. Keys are the file's presigned
  // URL (unique per object). All checkboxes share one selection set
  // across months so a single "Download selected" button drives the
  // whole batch_export download chain.
  const [selectedUrls, setSelectedUrls] = useState<Set<string>>(new Set());
  const [copyToast, setCopyToast] = useState<string | null>(null);

  const fetchUrls = async () => {
    try {
      const b = await getDownloadUrls(workflowId);
      setBundle(b);
    } catch (err: unknown) {
      setBundleErr(String(err));
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        mb={1}
      >
        <Typography variant="overline">
          Download links
          {bundle && ` (good for ${formatExpiry(bundle.expires_in)})`}
        </Typography>
        {!bundle && (
          <Button variant="contained" size="small" onClick={fetchUrls}>
            Get download links
          </Button>
        )}
        {bundle &&
          (() => {
            const items = bundleItems(bundle);
            const allKeys = items.map((i) => i.url);
            const allSelected =
              allKeys.length > 0 && allKeys.every((k) => selectedUrls.has(k));
            return (
              <Stack direction="row" spacing={1} alignItems="center">
                <Button
                  size="small"
                  onClick={() =>
                    setSelectedUrls(
                      allSelected ? new Set() : new Set(allKeys),
                    )
                  }
                >
                  {allSelected ? "Clear" : "Select all"}
                </Button>
                <Tooltip title="Copy the selected URLs (with filenames) to the clipboard — paste into an email / chat / curl script">
                  <span>
                    <Button
                      size="small"
                      startIcon={<ContentCopyIcon />}
                      disabled={selectedUrls.size === 0}
                      onClick={async () => {
                        const text = clipboardTextForUrls(
                          items,
                          selectedUrls,
                          bundle.expires_in,
                        );
                        try {
                          await navigator.clipboard.writeText(text);
                          setCopyToast(`Copied ${selectedUrls.size} URLs`);
                        } catch {
                          setCopyToast(
                            "Copy failed — clipboard requires HTTPS or a recent browser",
                          );
                        }
                      }}
                    >
                      Copy URLs
                    </Button>
                  </span>
                </Tooltip>
                <Button
                  variant="contained"
                  size="small"
                  disabled={selectedUrls.size === 0}
                  onClick={() =>
                    triggerDownloads(
                      items
                        .filter((i) => selectedUrls.has(i.url))
                        .map((i) => i.url),
                    )
                  }
                >
                  Download selected ({selectedUrls.size})
                </Button>
              </Stack>
            );
          })()}
      </Stack>
      <Snackbar
        open={!!copyToast}
        autoHideDuration={3000}
        onClose={() => setCopyToast(null)}
        message={copyToast}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
      {bundleErr && <Alert severity="error">{bundleErr}</Alert>}
      {bundle && bundle.months && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" />
              <TableCell>month</TableCell>
              <TableCell>files</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {bundle.months.map((m) => {
              const monthUrls: string[] = [
                ...m.parts.map((p) => p.url),
                ...(m.manifest_url ? [m.manifest_url] : []),
                ...(m.index_url ? [m.index_url] : []),
              ];
              const monthAll =
                monthUrls.length > 0 &&
                monthUrls.every((u) => selectedUrls.has(u));
              const monthSome = monthUrls.some((u) => selectedUrls.has(u));
              return (
                <TableRow key={m.month}>
                  <TableCell padding="checkbox">
                    {monthUrls.length > 0 && (
                      <Checkbox
                        size="small"
                        checked={monthAll}
                        indeterminate={monthSome && !monthAll}
                        onChange={(_, on) => {
                          setSelectedUrls((prev) => {
                            const next = new Set(prev);
                            for (const u of monthUrls) {
                              if (on) next.add(u);
                              else next.delete(u);
                            }
                            return next;
                          });
                        }}
                      />
                    )}
                  </TableCell>
                  <TableCell>{m.month}</TableCell>
                  <TableCell>
                    {monthUrls.length === 0 ? (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                      >
                        (empty)
                      </Typography>
                    ) : (
                      <Stack spacing={0.25}>
                        {m.parts.map((p) => {
                          const label = p.download_name || p.name || "(output)";
                          return (
                            <Stack
                              key={p.url}
                              direction="row"
                              spacing={1}
                              alignItems="center"
                            >
                              <Checkbox
                                size="small"
                                sx={{ p: 0.25 }}
                                checked={selectedUrls.has(p.url)}
                                onChange={(_, on) => {
                                  setSelectedUrls((prev) => {
                                    const next = new Set(prev);
                                    if (on) next.add(p.url);
                                    else next.delete(p.url);
                                    return next;
                                  });
                                }}
                              />
                              <Link
                                href={p.url}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {label}
                              </Link>
                            </Stack>
                          );
                        })}
                        {m.manifest_url && (
                          <SidecarRow
                            label={m.manifest_download_name || "_manifest.json"}
                            url={m.manifest_url}
                            selectedUrls={selectedUrls}
                            setSelectedUrls={setSelectedUrls}
                          />
                        )}
                        {m.index_url && (
                          <SidecarRow
                            label={m.index_download_name || "_index.parquet"}
                            url={m.index_url}
                            selectedUrls={selectedUrls}
                            setSelectedUrls={setSelectedUrls}
                          />
                        )}
                      </Stack>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
      {bundle && !bundle.months && (bundle.parts || bundle.manifest_url) && (
        <Stack spacing={0.5}>
          {bundle.parts?.map((p) => (
            <Stack
              key={p.url}
              direction="row"
              spacing={1}
              alignItems="center"
            >
              <Checkbox
                size="small"
                sx={{ p: 0.25 }}
                checked={selectedUrls.has(p.url)}
                onChange={(_, on) => {
                  setSelectedUrls((prev) => {
                    const next = new Set(prev);
                    if (on) next.add(p.url);
                    else next.delete(p.url);
                    return next;
                  });
                }}
              />
              <Link href={p.url} target="_blank" rel="noopener noreferrer">
                {p.download_name || p.name || "(output)"}
              </Link>
            </Stack>
          ))}
          {bundle.manifest_url && (
            <SidecarRow
              label={bundle.manifest_download_name || "_manifest.json"}
              url={bundle.manifest_url}
              selectedUrls={selectedUrls}
              setSelectedUrls={setSelectedUrls}
            />
          )}
          {bundle.index_url && (
            <SidecarRow
              label={bundle.index_download_name || "_index.parquet"}
              url={bundle.index_url}
              selectedUrls={selectedUrls}
              setSelectedUrls={setSelectedUrls}
            />
          )}
        </Stack>
      )}
    </Paper>
  );
}

// Sidecar row (manifest / index). Same shape as the parts rows but
// with an explicit label since the sidecar URLs aren't in the parts[]
// array (they're separate fields on DownloadBundle).
function SidecarRow({
  label,
  url,
  selectedUrls,
  setSelectedUrls,
}: {
  label: string;
  url: string;
  selectedUrls: Set<string>;
  setSelectedUrls: (fn: (prev: Set<string>) => Set<string>) => void;
}) {
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Checkbox
        size="small"
        sx={{ p: 0.25 }}
        checked={selectedUrls.has(url)}
        onChange={(_, on) => {
          setSelectedUrls((prev) => {
            const next = new Set(prev);
            if (on) next.add(url);
            else next.delete(url);
            return next;
          });
        }}
      />
      <Link href={url} target="_blank" rel="noopener noreferrer">
        {label}
      </Link>
    </Stack>
  );
}
